import { onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";

import { dialerApi } from "@/services/dialer-api";

import type { CallState } from "@/services/dialer-api";

interface UseDialerConnectionOptions {
  autoConnect?: boolean;
  onCallStateUpdate?: (callState: CallState | null) => void;
}

/**
 * Composable for connecting the dashboard to the Twilio V2 backend.
 *
 * Subscribes to WebSocket channels for call state updates,
 * registers the browser as an online dialer, and forwards
 * call state changes to the provided callback.
 */
export function useDialerConnection(options: UseDialerConnectionOptions = {}) {
  const store = useStore();

  const isInitializing = ref(false);

  async function initialize(): Promise<boolean> {
    if (isInitializing.value) {
      return false;
    }

    isInitializing.value = true;

    try {
      const user = store.getters["auth/user"];

      if (!user?.id) {
        return false;
      }

      await dialerApi.connect(user.id);
      return true;
    } catch {
      return false;
    } finally {
      isInitializing.value = false;
    }
  }

  // Watch for call state changes and notify
  const stopCallStateWatch = watch(
    () => dialerApi.callState.value,
    (newState) => {
      options.onCallStateUpdate?.(newState);
    }
  );

  onUnmounted(() => {
    stopCallStateWatch();
  });

  return {
    initialize,
  };
}
